import { formatUsageValue } from '@/core/formatNumber';
import { translate } from '@/i18n';
import { Field } from '@/resource/summary';

import { getUsagePercentOfLimitComponent } from './QuotaCell';
import { QuotaProgressBar } from './QuotaProgressBar';

interface QuotaSingleView {
  usage: number | string;
  limit?: number | string;
  title: any;
  billingType?: string;
  limitFrequency?: string;
  displayUnit?: string;
  // When a spending limit's period starts from zero.
  resets?: string;
  // Two components share the width, so details stack as label/value rows
  // instead of spreading out in columns.
  stacked?: boolean;
}

export const QuotaSingleView = ({
  usage,
  limit,
  title,
  billingType,
  limitFrequency,
  displayUnit,
  resets,
  stacked,
}: QuotaSingleView) => {
  const percent = getUsagePercentOfLimitComponent(limit, usage);

  const details = [
    // Volumes carry no billing settings, only a size.
    billingType && { label: translate('Type'), value: billingType },
    displayUnit && { label: translate('Unit'), value: displayUnit },
    limitFrequency && {
      label: translate('Limit period'),
      value: limitFrequency,
    },
    resets && { label: translate('Resets'), value: resets },
  ].filter(Boolean);

  return (
    <div className="flex flex-col">
      <span className="font-medium text-primary whitespace-nowrap ellipsis">
        {limit
          ? `${formatUsageValue(usage)} / ${formatUsageValue(limit)}`
          : formatUsageValue(usage)}{' '}
        {title}
      </span>
      <QuotaProgressBar
        percent={percent}
        className="mt-1.5"
        label={typeof title === 'string' ? title : undefined}
      />
      {stacked ? (
        <div className="mt-2.5">
          {details.map((detail) => (
            <Field
              key={detail.label}
              label={detail.label}
              value={detail.value}
              labelCol={6}
              valueCol={6}
            />
          ))}
        </div>
      ) : (
        <div className="flex flex-wrap *:flex-1 mt-2.5 gap-x-5">
          {details.map((detail) => (
            <div key={detail.label}>
              <span className="text-secondary font-medium block mb-1">
                {detail.label}:
              </span>
              <span className="text-quaternary">{detail.value}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
